import { Video } from "@/api";
import { useVideoFiles } from "@/hooks";
import { useEffect, useMemo, useState } from "react";
import { getDateFromUTCString } from "@/utils";
import { addMinutes, isBefore } from "date-fns";

interface UseLivestreamProps {
  video?: Video;
  videoId?: number;
}

export const useLivestream = ({ video, videoId }: UseLivestreamProps) => {
  const { isLive, liveStatus } = useVideoFiles({ video, videoId });

  const [livePlayable, setLivePlayable] = useState<boolean>(false);

  // Check Video Properties to know if its a livestream
  const propertyIsLiveEnded =
    ["true", "1"].includes(video?.Properties?.LiveEventEnded || "") &&
    (!video?.DurationSeconds || video.DurationSeconds === "0");

  // Also check the status of the livestream, in case the video property hasn't been updated yet (e.g. if the stream has only just finished)
  const statusIsLiveEnded = [
    "stopping",
    "stopped",
    "destroying",
    "capturing",
    "completed",
  ].includes(liveStatus ?? "");

  const isLiveEnded = propertyIsLiveEnded || statusIsLiveEnded;
  const liveStartString = !isLiveEnded
    ? video?.Properties?.LiveEventStartDate
    : undefined;

  const liveStartDate = useMemo(() => {
    // LiveEventStartDate is in UTC time
    if (liveStartString) {
      return getDateFromUTCString(liveStartString);
    }
  }, [liveStartString]);

  // Controls whether the livestream countdown is showing
  const isUpcomingLive = useMemo(() => {
    if (!liveStartDate) return false; // Not a live video
    if (livePlayable) return false; // The live has already started
    return liveStartDate > new Date();
  }, [liveStartDate, livePlayable]);

  useEffect(() => {
    if (liveStartDate && !livePlayable) {
      const thirtyMinutesLater = addMinutes(new Date(), 30);

      const startPolling = () => {
        // Check if the HLS file is playable
        isLive().then((il) => setLivePlayable(il));
        const intervalId = setInterval(() => {
          isLive().then((il) => {
            if (il) {
              // If the video changes to live during polling, wait 10 seconds before setting livePlayable to true
              // There is sometimes a delay between the stream URL being live and JWPlayer being ready to play the stream
              setTimeout(() => {
                setLivePlayable(il);
              }, 10000);
            } else {
              setLivePlayable(il);
            }
          });
        }, 5000);
        return intervalId;
      };

      if (isBefore(liveStartDate, thirtyMinutesLater)) {
        // Within 30 minutes - start polling immediately
        const intervalId = startPolling();
        return () => clearInterval(intervalId);
      } else {
        // Calculate milliseconds until we're 30 minutes away
        const msUntilThirtyMin =
          liveStartDate.getTime() - thirtyMinutesLater.getTime();
        // Set up a timeout to start polling at exactly 30 minutes before
        const timeoutId = setTimeout(() => {
          const intervalId = startPolling();
          return () => clearInterval(intervalId);
        }, msUntilThirtyMin);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [isLive, liveStartDate, livePlayable]);

  return { isUpcomingLive, liveStartDate, isLiveEnded };
};
